import type { AdminCompanyDetailDto, AdminParentOrganizationSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { EditSectionControls } from '@components/shared/edit-section-controls.component';
import { SectionCard } from '@components/shared/section-card.component';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useClipboardCopy } from '@hooks/use-clipboard-copy.hook';
import {
  useAdminOrganizationHierarchyOptionsQuery,
  useAdminUpdateOrganizationHierarchy,
} from '@queries/admin-organizations.query';
import { adminOrganizationHierarchySchema } from '@schemas/admin-organization.schema';
import { useStore } from '@tanstack/react-form';
import { createFormInvalidHandler } from '@utils/form.util';
import { CheckIcon, CopyIcon, XIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatOrganizationShortId, getAdminOrganizationHref } from '../organization-hierarchy.utils';
import {
  AdminOrganizationHierarchyFormData,
  adminOrganizationHierarchyFormOptions,
} from './admin-organizations-form-options';

type AdminOrganizationHierarchyTileProps = {
  isDeleted: boolean;
  organization: AdminCompanyDetailDto;
  organizationId: string;
};

const PARENT_ORGANIZATION_ENABLE_CHILDREN_TOOLTIP = 'Enable child organizations for this organization first.';

const buildHierarchyDraftValues = (params: {
  initialChildOrganizationIds: string[];
  isParentOrganization: boolean;
  parentOrganizationId: string | null;
}): AdminOrganizationHierarchyFormData => {
  const { initialChildOrganizationIds, isParentOrganization, parentOrganizationId } = params;

  return {
    childOrganizationIds: initialChildOrganizationIds,
    draftIsParentOrganization: isParentOrganization,
    draftParentOrganizationIds: parentOrganizationId ? [parentOrganizationId] : [],
  };
};

export const AdminOrganizationHierarchyTile: React.FC<AdminOrganizationHierarchyTileProps> = (props) => {
  const { isDeleted, organization, organizationId } = props;

  const [isEditing, setIsEditing] = useState(false);
  const { handleCopy, isCopied } = useClipboardCopy();
  const { data: hierarchyOptionsData } = useAdminOrganizationHierarchyOptionsQuery(organizationId);
  const { isPending: isUpdatingHierarchy, mutateAsync: updateOrganizationHierarchy } =
    useAdminUpdateOrganizationHierarchy();

  const initialChildOrganizationIds = useMemo(
    () => (organization.childOrganizations ?? []).map((childOrganization) => childOrganization.id),
    [organization.childOrganizations],
  );
  const normalizedParentOrganizationId =
    typeof organization.parentOrganizationId === 'string' ? organization.parentOrganizationId : null;
  const defaultHierarchyValues = useMemo(
    () =>
      buildHierarchyDraftValues({
        initialChildOrganizationIds,
        isParentOrganization: organization.isParentOrganization,
        parentOrganizationId: normalizedParentOrganizationId,
      }),
    [initialChildOrganizationIds, normalizedParentOrganizationId, organization.isParentOrganization],
  );

  const form = useAppForm({
    ...adminOrganizationHierarchyFormOptions,
    defaultValues: defaultHierarchyValues,
    onSubmit: async ({ value }) => {
      const normalizedChildOrganizationIds = value.draftIsParentOrganization ? value.childOrganizationIds : [];
      const normalizedDraftParentOrganizationId = value.draftIsParentOrganization
        ? null
        : (value.draftParentOrganizationIds[0] ?? null);

      try {
        await updateOrganizationHierarchy({
          data: {
            childOrganizationIds: normalizedChildOrganizationIds,
            isParentOrganization: value.draftIsParentOrganization,
            parentOrganizationId: normalizedDraftParentOrganizationId,
          },
          id: organizationId,
        });
        successToast({ description: 'Organization hierarchy updated successfully.' });
        setIsEditing(false);
      } catch (error) {
        if (error instanceof Error) {
          errorToast({ description: error.message });
          return;
        }

        errorToast({ description: 'Failed to update organization hierarchy.' });
      }
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: adminOrganizationHierarchySchema,
    },
  });

  const childOrganizationIds = useStore(form.store, (state) => state.values.childOrganizationIds ?? []);
  const draftIsParentOrganization = useStore(form.store, (state) => state.values.draftIsParentOrganization);

  const childOrganizationOptions = hierarchyOptionsData?.childOrganizations ?? [];
  const parentOrganizationOptions = hierarchyOptionsData?.parentOrganizations ?? [];
  const selectableParentOrganizationOptions = useMemo(
    () =>
      parentOrganizationOptions.map((organizationOption) => ({
        ...organizationOption,
        description: organizationOption.isParentOrganization ? undefined : PARENT_ORGANIZATION_ENABLE_CHILDREN_TOOLTIP,
        disabled: !organizationOption.isParentOrganization,
      })),
    [parentOrganizationOptions],
  );

  const organizationOptionMap = useMemo(() => {
    const organizationMap = new Map<string, AdminParentOrganizationSelectDto>();
    const allOrganizations = [
      ...childOrganizationOptions,
      ...parentOrganizationOptions,
      ...(organization.childOrganizations ?? []),
      ...(organization.parentOrganization ? [organization.parentOrganization] : []),
    ];

    for (const organizationOption of allOrganizations) {
      organizationMap.set(organizationOption.id, organizationOption);
    }

    return organizationMap;
  }, [
    childOrganizationOptions,
    organization.childOrganizations,
    organization.parentOrganization,
    parentOrganizationOptions,
  ]);

  const childOrganizations = useMemo(() => {
    const currentChildOrganizationIds = isEditing ? childOrganizationIds : initialChildOrganizationIds;
    const organizations: AdminParentOrganizationSelectDto[] = [];

    for (const childOrganizationId of currentChildOrganizationIds) {
      const childOrganization = organizationOptionMap.get(childOrganizationId);
      if (childOrganization) {
        organizations.push(childOrganization);
      }
    }

    return organizations;
  }, [childOrganizationIds, initialChildOrganizationIds, isEditing, organizationOptionMap]);

  const selectedParentOrganization =
    organization.parentOrganization ??
    (normalizedParentOrganizationId ? organizationOptionMap.get(normalizedParentOrganizationId) : undefined);

  const isBusy = isUpdatingHierarchy;
  const isEffectiveParentOrganization = isEditing ? draftIsParentOrganization : organization.isParentOrganization;

  const handleCancelEdit = (): void => {
    form.reset(defaultHierarchyValues);
    setIsEditing(false);
  };

  return (
    <SectionCard>
      <EditSectionControls
        cancelAriaLabel="Cancel Organization Hierarchy"
        editAriaLabel="Edit Organization Hierarchy"
        editDisabledTooltip={isDeleted ? 'Cannot edit deleted organization' : undefined}
        isCancelDisabled={isBusy}
        isEditDisabled={isDeleted}
        isEditing={isEditing}
        isSaveDisabled={isBusy}
        onCancel={handleCancelEdit}
        onEdit={() => {
          form.reset(defaultHierarchyValues);
          setIsEditing(true);
        }}
        onSave={() => form.handleSubmit()}
        saveAriaLabel="Save Organization Hierarchy"
      />

      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-gray-900">Organization Hierarchy</h3>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppForm>
            {isEditing ? (
              <div className="flex flex-col gap-4">
                <form.AppField
                  listeners={{
                    onChange: ({ value }) => {
                      if (value) {
                        form.setFieldValue('draftParentOrganizationIds', []);
                      }
                    },
                  }}
                  name="draftIsParentOrganization"
                >
                  {(field) => (
                    <field.CheckboxField
                      disabled={isBusy}
                      label="Enable Child Organizations"
                    />
                  )}
                </form.AppField>

                {!draftIsParentOrganization && (
                  <form.AppField
                    listeners={{
                      onChange: ({ value }) => {
                        const selectedParentOrganizationIds = value ?? [];
                        if (selectedParentOrganizationIds.length <= 1) {
                          return;
                        }

                        const latestParentOrganizationId =
                          selectedParentOrganizationIds[selectedParentOrganizationIds.length - 1];
                        if (!latestParentOrganizationId) {
                          return;
                        }

                        form.setFieldValue('draftParentOrganizationIds', [latestParentOrganizationId]);
                      },
                    }}
                    name="draftParentOrganizationIds"
                    validators={{ onBlur: adminOrganizationHierarchySchema.shape.draftParentOrganizationIds }}
                  >
                    {(field) => (
                      <field.SelectMultipleField
                        disabled={isBusy}
                        label="Parent Organization"
                        options={selectableParentOrganizationOptions}
                        placeholder="Select parent organization"
                      />
                    )}
                  </form.AppField>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Enable Child Organizations</span>
                  <span className="text-sm text-gray-900">{organization.isParentOrganization ? 'Yes' : 'No'}</span>
                </div>

                {!organization.isParentOrganization && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-500">Parent Organization</span>
                    {selectedParentOrganization ? (
                      <Link
                        className="w-fit text-sm text-blue-700 hover:underline"
                        to={getAdminOrganizationHref(selectedParentOrganization.id)}
                      >
                        {selectedParentOrganization.name}
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-900">—</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {isEffectiveParentOrganization && (
              <div className="mt-4 flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-500">Child Organizations</span>

                {isEditing && (
                  <form.AppField
                    name="childOrganizationIds"
                    validators={{ onBlur: adminOrganizationHierarchySchema.shape.childOrganizationIds }}
                  >
                    {(field) => (
                      <field.SelectMultipleField
                        disabled={isBusy}
                        label=""
                        options={childOrganizationOptions}
                        placeholder="Select child organizations"
                      />
                    )}
                  </form.AppField>
                )}

                {childOrganizations.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                    <div className="flex flex-col divide-y divide-gray-100">
                      {childOrganizations.map((childOrganization) => (
                        <div
                          className="flex items-center justify-between gap-2 px-3 py-2"
                          key={childOrganization.id}
                        >
                          <div className="flex min-w-0 flex-col">
                            <Link
                              className="truncate text-sm text-blue-700 hover:underline"
                              to={getAdminOrganizationHref(childOrganization.id)}
                            >
                              {childOrganization.name}
                            </Link>
                            <span className="text-xs text-gray-500 font-mono">
                              {formatOrganizationShortId(childOrganization.id)}
                            </span>
                          </div>

                          <div className="flex items-center gap-1">
                            <IconButton
                              ariaLabel="Copy child organization ID"
                              className="border border-gray-200"
                              icon={isCopied(`child-${childOrganization.id}`) ? <CheckIcon /> : <CopyIcon />}
                              onClick={() => handleCopy(`child-${childOrganization.id}`, childOrganization.id)}
                              size="sm"
                              variant="secondary"
                            />
                            {isEditing && (
                              <IconButton
                                ariaLabel="Remove child organization"
                                className="border border-gray-200"
                                disabled={isBusy}
                                icon={<XIcon className="size-3.5" />}
                                onClick={() => {
                                  form.setFieldValue(
                                    'childOrganizationIds',
                                    childOrganizationIds.filter(
                                      (childOrganizationId) => childOrganizationId !== childOrganization.id,
                                    ),
                                  );
                                }}
                                size="sm"
                                variant="secondary"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">No child organizations assigned.</span>
                )}
              </div>
            )}
          </form.AppForm>
        </form>
      </div>
    </SectionCard>
  );
};
